import { Show, createEffect, createSignal, onCleanup, untrack } from "solid-js";
import { focusablesIn, sealBackground } from "@/shared/ui/modalFocus";
import { Portal } from "solid-js/web";
import { ChevronDown } from "lucide-solid";
import { cn } from "@/shared/ui/utils";
import { t } from "@/shared/i18n";
import {
  accept,
  acceptAll,
  closeSettings,
  consentSettingsOpen,
  hasMarketing,
  hasStatistics,
  needsDecision,
  rejectAll,
  revoke,
} from "@/tracking/consent/consentStore";

/**
 * The consent dialog (CMP surface). Mounted once, next to ToastContainer in the app Layout.
 *
 * <p>It renders while a decision is outstanding, and again whenever the privacy link reopens it. The
 * store owns what a decision MEANS — this file owns only how it is asked for, and the asking is the
 * part a supervisory authority reads.</p>
 *
 * <p>Vendors are named from <c>activeVendors</c>, which the caller derives from the tenant's own
 * tracking configuration. It is not a decorative list: naming a processor the tenant does not
 * actually use makes the disclosure false, so the sentence disappears entirely when the list is empty
 * rather than falling back to a generic "partnere". An absent prop means no vendors, so a caller that
 * has not wired the tenant config yet under-claims instead of over-claiming.</p>
 *
 * <h2>THIS IS A CHOICE GATE, NOT A CONSENT WALL — do not "simplify" it into one</h2>
 *
 * <p>The dialog is modal: it is centred, the page behind it is dimmed, blurred and <c>inert</c>, and
 * nothing on the site can be reached until one of the headline choices is clicked. Blocking the page
 * until a CHOICE is made is lawful. Blocking it until CONSENT is given is not — that is the whole
 * distance between a valid consent and an invalid one, and the two look nearly identical in a
 * screenshot. Three properties keep this one on the lawful side, and every one of them is a one-line
 * edit away from being lost:</p>
 *
 * <ol>
 *   <li><b>The two headline choices are equal.</b> "Kun nødvendig" and "Godta alle" share the
 *       <c>ACTION_BUTTON</c> constant, so they have the same size, padding, font weight, shape and
 *       flex basis, and both are filled surfaces with comparable contrast. Neither may become a link,
 *       a ghost button, a text button, or anything else that reads as the lesser option while the
 *       other reads as the primary one. Consent is not freely given when refusing costs more effort
 *       or attention than accepting (GDPR art. 4(11), art. 7(4), EDPB 03/2022 on deceptive design).
 *       Both are visible at the same time, above the fold, one click each.</li>
 *   <li><b>"Kun nødvendig" releases the gate completely.</b> It writes a real decision — a refusal is
 *       an ANSWER, not a deferral — the overlay unmounts, the background stops being inert, and the
 *       visitor gets the entire service, unimpaired. Refusing must never lead to a reduced service, a
 *       repeated ask, a nag, or a second dialog.</li>
 *   <li><b>There is no path forward that requires acceptance.</b> Every exit from this dialog
 *       (necessary-only, accept-all, save-selection) fully releases the gate. If you ever find
 *       yourself adding a route out of here that only opens on consent, you have built a cookie wall,
 *       and this component is the wrong place to put it.</li>
 * </ol>
 *
 * <p>A pre-ticked switch would break the same rule from the inside, which is why both optional
 * categories start off and the tests assert it.</p>
 */
interface ConsentBannerProps {
  /** Processors actually enabled for this tenant, in display order, e.g. ["Google", "Meta"]. */
  activeVendors?: string[];
}

// ── Shared button shape ──────────────────────────────────────────────────────

/**
 * Geometry and weight for BOTH headline choices. One constant, not two similar strings, because the
 * requirement is that they are indistinguishable in prominence — and a shared constant is the only
 * version of that which cannot drift when one of them is later restyled. <c>sm:flex-1</c> makes them
 * literally the same width on a wide dialog rather than merely similar.
 */
const ACTION_BUTTON =
  "inline-flex w-full items-center justify-center rounded-lg border px-5 py-3 text-[15px] " +
  "font-semibold leading-tight text-white transition-all focus-visible:outline " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white " +
  "sm:flex-1 sm:min-w-[12rem]";

/** Filled, not ghosted: the refusal must not read as the lesser option. White on it is roughly 13:1. */
const NEUTRAL_SURFACE = "border-white/20 bg-white/10 hover:border-white/30 hover:bg-white/15";

/**
 * The accent fill uses <c>--accent-hover</c> (the tenant's PrimaryColorDark), not <c>--accent</c>.
 * White on the resting accent is 3.6:1 for the LOBAS teal and 4.7:1 for the XL-BYGG red — the teal
 * fails AA at this text size. The dark shade of the same brand colour clears it for both (5.3:1 and
 * 6.6:1), and hover lifts brightness by only 5% so the hovered state stays above 4.5:1 as well; the
 * ring carries the rest of the hover affordance.
 */
const ACCENT_SURFACE =
  "border-transparent bg-[var(--accent-hover)] hover:brightness-105 hover:ring-2 hover:ring-white/25";

// ── Modal plumbing ───────────────────────────────────────────────────────────

// ── Text helpers ─────────────────────────────────────────────────────────────

/** Norwegian list: "Google", "Google og Meta", "Google, Meta og TikTok". */
function formatVendors(vendors: string[]): string {
  if (vendors.length <= 1) return vendors[0] ?? "";
  return `${vendors.slice(0, -1).join(", ")} og ${vendors[vendors.length - 1]}`;
}

// ── Dialog ───────────────────────────────────────────────────────────────────

export default function ConsentBanner(props: ConsentBannerProps) {
  const [expanded, setExpanded] = createSignal(false);
  const [statistics, setStatistics] = createSignal(false);
  const [marketing, setMarketing] = createSignal(false);
  const [dialog, setDialog] = createSignal<HTMLDivElement>();

  const vendors = () => props.activeVendors ?? [];
  const vendorList = () => formatVendors(vendors());

  const visible = () => needsDecision() || consentSettingsOpen();
  const isExpanded = () => expanded() || consentSettingsOpen();

  /**
   * Which surface the receipt records. The same three buttons serve the first ask and the reopened
   * panel, and "how was this collected" is a question the record has to answer on its own.
   */
  const source = () => (consentSettingsOpen() ? "settings" : "banner");

  /**
   * Reopened from the privacy link, the switches must show what is actually stored — otherwise the
   * panel invites someone to "lagre" a pair of off switches they never set, silently withdrawing a
   * consent they came only to review. Read untracked: this syncs on the transition to open, and
   * re-running it on every store write would yank the switches back mid-edit.
   */
  createEffect(() => {
    if (!consentSettingsOpen()) return;
    setExpanded(true);
    untrack(() => {
      setStatistics(hasStatistics());
      setMarketing(hasMarketing());
    });
  });

  /**
   * Open: seal the background, move focus in, and remember where focus came from so it can be given
   * back. Closing re-runs this effect (it reads <c>visible()</c>), which fires the cleanup below.
   */
  createEffect(() => {
    if (!visible()) return;
    // isConnected guards the one frame where the signal still holds the previous, detached panel
    // after a close/reopen: sealing from a detached node would find no branch to spare.
    const el = dialog();
    if (!el?.isConnected) return;

    const restoreTo = document.activeElement as HTMLElement | null;
    const release = sealBackground(el);

    // The container itself, not the first button: landing on a control would skip the disclosure the
    // dialog is labelled and described by, and would put focus on one of the two choices — which is
    // a thumb on the scale for whichever one happens to be first in the DOM.
    queueMicrotask(() => el.focus());

    onCleanup(() => {
      release();
      // Only if focus is still ours to give back; the page may have moved it on in the meantime.
      if (restoreTo?.isConnected && document.activeElement === document.body) restoreTo.focus();
    });
  });

  function collapse() {
    setExpanded(false);
    closeSettings();
  }

  const chooseNecessary = () => {
    // Releases the gate completely — see the class comment. rejectAll writes a decision, so
    // needsDecision() goes false, the overlay unmounts and the page behind it is handed back whole.
    rejectAll(source());
    collapse();
  };

  const chooseAll = () => {
    acceptAll(source());
    collapse();
  };

  const saveChoice = () => {
    accept({ statistics: statistics(), marketing: marketing() }, source());
    collapse();
  };

  /**
   * "Skjul valg" collapses the detail rows on the first ask, and closes the whole panel when it was
   * the privacy link that opened it — there the rows ARE the panel, so hiding them leaves an empty
   * dialog holding the page hostage for a question that was already answered.
   */
  const toggleDetails = () => (isExpanded() ? collapse() : setExpanded(true));

  /**
   * Escape does NOT close the gate while a decision is outstanding.
   *
   * <p>That is the opposite of the usual modal rule, and it is deliberate: dismissing would be a
   * choice without choosing. There is nothing to fall back TO, so the visitor would be left both
   * unasked and untracked — an implicit refusal we never recorded — and the dialog would return on
   * the next render anyway. Since the page behind is inert, an Escape that "closed" it would hand
   * back a screen with no way to answer the question still blocking it.</p>
   *
   * <p>Nobody is trapped: two equal one-click answers are on screen, and either releases the gate.</p>
   *
   * <p>Once a decision IS on record the dialog is no longer a gate — it is the review panel the
   * privacy link opens — and there Escape closes it, because leaving without touching anything is a
   * real and correct outcome. Removing that would force someone who came only to look to write a new
   * receipt in order to get out.</p>
   */
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Escape" || !visible() || needsDecision()) return;
    e.preventDefault();
    collapse();
  };

  document.addEventListener("keydown", onKeyDown);
  onCleanup(() => document.removeEventListener("keydown", onKeyDown));

  /**
   * Focus trap. Defensible only because the rest of the page is inert: there is nowhere outside the
   * dialog that Tab could usefully land, so a ring is the honest behaviour rather than a cage.
   */
  const onDialogKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const el = dialog();
    if (!el) return;

    const items = focusablesIn(el);
    if (items.length === 0) {
      e.preventDefault();
      el.focus();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (!el.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }
    if (e.shiftKey && (active === first || active === el)) {
      e.preventDefault();
      last.focus();
      return;
    }
    if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <Show when={visible()}>
      {/* Portalled to <body> so the overlay is a sibling of the app root rather than a descendant:
          that is what lets the whole app go inert without taking the dialog with it. */}
      <Portal>
        {/* Above the price bar (z-50) and the toasts (z-60): nothing may cover the choice. */}
        <div class="consent-gate fixed inset-0 z-[80] flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[max(1rem,env(safe-area-inset-top))]">
          <style>{`
            @media (prefers-reduced-motion: no-preference) {
              .consent-gate       { animation: consent-fade .2s ease-out both }
              .consent-gate-panel { animation: consent-zoom .28s cubic-bezier(.2,.8,.25,1) both }
              @keyframes consent-fade { from { opacity: 0 } to { opacity: 1 } }
              @keyframes consent-zoom {
                from { transform: translateY(8px) scale(.98); opacity: 0 }
                to   { transform: translateY(0)   scale(1);   opacity: 1 }
              }
            }
            @media (prefers-reduced-motion: reduce) {
              .consent-gate, .consent-gate * { animation: none !important; transition: none !important }
            }
          `}</style>

          {/* The dim + blur is its OWN layer behind the panel, not a filter on the panel's box.
              backdrop-blur on the dialog would be read by half the room as "the text is fuzzy". */}
          <div
            aria-hidden="true"
            class="absolute inset-0 bg-black/70 backdrop-blur-md backdrop-saturate-50"
          />

          <div
            ref={setDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-dialog-title"
            aria-describedby="consent-dialog-body"
            tabindex="-1"
            onKeyDown={onDialogKeyDown}
            class="consent-gate-panel relative max-h-full w-full max-w-[40rem] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-900 p-5 shadow-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:p-6"
          >
            {/* Visible now that this is a centred modal: a dialog that takes the whole screen and
                gives no name to what it is asking about is the part people click past. */}
            <h2 id="consent-dialog-title" class="text-lg font-semibold text-white">
              {t("consent.title", "Informasjonskapsler")}
            </h2>

            <p id="consent-dialog-body" class="mt-3 text-[15px] leading-relaxed text-zinc-300">
              {t(
                "consent.body",
                "Vi bruker informasjonskapsler for statistikk og markedsføring, i tillegg til nødvendige kapsler for å levere tjenesten.",
              )}{" "}
              <a
                href="/personvern"
                class="font-medium text-white underline underline-offset-2 transition-colors hover:text-zinc-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {t("consent.readMore", "Les mer")}
              </a>
            </p>

            <Show when={vendors().length > 0}>
              <p class="mt-2 text-[13px] leading-relaxed text-zinc-400">
                {t("consent.vendorsLead", "Ved samtykke kan opplysninger om bruken deles med")}{" "}
                {vendorList()}.
              </p>
            </Show>

            <Show when={isExpanded()}>
              <div id="consent-details" class="mt-5 flex flex-col gap-2.5">
                {/* No switch, because there is nothing to choose — a control that cannot be turned off
                    invites the click that proves it. The state is stated in words instead. */}
                <div
                  id="consent-row-necessary"
                  class="flex items-start justify-between gap-4 rounded-lg border border-white/10 bg-white/[0.04] p-4"
                >
                  <div class="min-w-0">
                    <p class="text-[15px] font-medium text-white">
                      {t("consent.necessary", "Nødvendige")}
                    </p>
                    <p class="mt-1 text-[13px] leading-relaxed text-zinc-400">
                      {t(
                        "consent.necessaryDesc",
                        "Innlogging, sikkerhet og grunnleggende funksjonalitet",
                      )}
                    </p>
                  </div>
                  <span class="mt-0.5 shrink-0 text-[13px] font-medium text-zinc-400">
                    {t("consent.always", "Alltid på")}
                  </span>
                </div>

                <ConsentRow
                  id="consent-statistics"
                  rowId="consent-row-statistics"
                  title={t("consent.statistics", "Statistikk")}
                  description={t(
                    "consent.statisticsDesc",
                    "Hjelper oss å forstå hvordan besøkende bruker nettstedet (Google Analytics)",
                  )}
                  checked={statistics()}
                  onChange={setStatistics}
                />

                <ConsentRow
                  id="consent-marketing"
                  rowId="consent-row-marketing"
                  title={t("consent.marketing", "Markedsføring")}
                  description={
                    t(
                      "consent.marketingDesc",
                      "Brukes til å måle annonser og vise mer relevante annonser",
                    ) + (vendors().length > 0 ? ` (${vendorList()})` : "")
                  }
                  checked={marketing()}
                  onChange={setMarketing}
                />

                <div class="mt-1 flex justify-end">
                  <button
                    type="button"
                    onClick={saveChoice}
                    class={cn(ACTION_BUTTON, ACCENT_SURFACE, "sm:flex-none")}
                  >
                    {t("consent.save", "Lagre valg")}
                  </button>
                </div>

                {/* Withdrawing is not an answer — it erases the answer, and the dialog asks again. The privacy
                    statement promises it here, under the settings, so this is where it is. */}
                <Show when={!needsDecision()}>
                  <div class="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => revoke()}
                      class="rounded py-1 text-[13px] text-zinc-400 underline underline-offset-4 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    >
                      {t("consent.withdraw", "Trekk tilbake samtykket")}
                    </button>
                  </div>
                </Show>
              </div>
            </Show>

            {/* "Tilpass valg" sits on its own line so the two headline choices below can share the
                width evenly. Putting it in the same row would push one of them narrower than the
                other, which is precisely the difference that is not allowed to exist. */}
            <div class="mt-5 flex justify-center sm:justify-start">
              <button
                type="button"
                onClick={toggleDetails}
                aria-expanded={isExpanded()}
                aria-controls="consent-details"
                class="inline-flex items-center justify-center gap-1.5 rounded py-1.5 text-[14px] font-medium text-zinc-300 underline underline-offset-4 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {isExpanded()
                  ? t("consent.hideChoices", "Skjul valg")
                  : t("consent.customise", "Tilpass valg")}
                <ChevronDown
                  aria-hidden="true"
                  class={cn("h-4 w-4 transition-transform", isExpanded() && "rotate-180")}
                />
              </button>
            </div>

            {/* Refusal comes before acceptance in the tab order, and both are one click from here. */}
            <div class="mt-3 flex flex-col gap-3 sm:flex-row sm:items-stretch">
              <button
                type="button"
                onClick={chooseNecessary}
                class={cn(ACTION_BUTTON, NEUTRAL_SURFACE)}
              >
                {t("consent.necessaryOnly", "Kun nødvendig")}
              </button>

              <button type="button" onClick={chooseAll} class={cn(ACTION_BUTTON, ACCENT_SURFACE)}>
                {t("consent.acceptAll", "Godta alle")}
              </button>
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}

// ── One toggleable purpose ───────────────────────────────────────────────────

interface ConsentRowProps {
  id: string;
  rowId: string;
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * A real <c>input type="checkbox"</c> carrying <c>role="switch"</c>, not the app's Switch primitive.
 *
 * <p>The primitive is a button with aria-checked, which a screen reader announces but which no form
 * control API can read back — and consent is the one place where "what did the control actually say"
 * has to be answerable from the DOM itself. A checkbox also gets space-to-toggle and label-click for
 * free, both of which the button version has to re-implement.</p>
 *
 * <p>The track and the thumb are SIBLINGS of the input rather than one nested in the other, because
 * Tailwind's <c>peer-checked:</c> compiles to a subsequent-sibling selector; a thumb inside the track
 * would stay stuck in the off position.</p>
 */
function ConsentRow(props: ConsentRowProps) {
  return (
    <div
      id={props.rowId}
      class="flex items-start justify-between gap-4 rounded-lg border border-white/10 bg-white/[0.04] p-4"
    >
      <div class="min-w-0">
        <label for={props.id} class="block cursor-pointer text-[15px] font-medium text-white">
          {props.title}
        </label>
        <p id={`${props.id}-desc`} class="mt-1 text-[13px] leading-relaxed text-zinc-400">
          {props.description}
        </p>
      </div>

      <label class="relative mt-0.5 inline-flex h-6 w-11 shrink-0 cursor-pointer items-center">
        <input
          id={props.id}
          type="checkbox"
          role="switch"
          class="peer sr-only"
          aria-describedby={`${props.id}-desc`}
          checked={props.checked}
          onChange={(e) => props.onChange(e.currentTarget.checked)}
        />
        {/* zinc-500 off, accent on: both clear 3:1 against the row, so the state is visible to
            someone who cannot separate the two hues — and the thumb position says it as well. */}
        <span
          aria-hidden="true"
          class="absolute inset-0 rounded-full bg-zinc-500 transition-colors peer-checked:bg-[var(--accent)] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-white"
        />
        <span
          aria-hidden="true"
          class="pointer-events-none absolute left-[3px] top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"
        />
      </label>
    </div>
  );
}
